import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UnsubscribePage = () => {
  const [params] = useSearchParams();
  const email = (params.get('email') || '').trim();
  const token = params.get('t') || params.get('token') || '';
  const [status, setStatus] = useState('idle');
  const [isSuppressed, setIsSuppressed] = useState(false);

  const check = async () => {
    if (!email) return;
    try {
      const r = await axios.get(`${API}/unsubscribe/check/${encodeURIComponent(email)}`);
      setIsSuppressed(!!r.data.is_unsubscribed);
    } catch {
      /* non-fatal */
    }
  };

  useEffect(() => { check(); /* eslint-disable-next-line */ }, [email]);

  const submit = async () => {
    setStatus('loading');
    try {
      await axios.post(`${API}/unsubscribe`, { email, token });
      setStatus('done');
      setIsSuppressed(true);
      toast.success('You have been unsubscribed.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unsubscribe');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="unsubscribe-page">
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <Mail className="w-10 h-10 mx-auto text-amber-400" />
            <h1 className="text-2xl font-bold">Email preferences</h1>
            {!email && (
              <p className="text-sm text-muted-foreground">
                The unsubscribe link is missing an email parameter. Please use the link inside the email you received.
              </p>
            )}
            {email && (
              <>
                <p className="text-sm text-muted-foreground">
                  We received an unsubscribe request for <strong className="text-foreground" data-testid="unsubscribe-email">{email}</strong>.
                </p>
                {isSuppressed || status === 'done' ? (
                  <div className="flex items-center justify-center gap-2 text-green-400 text-sm" data-testid="unsubscribe-success">
                    <CheckCircle2 className="w-5 h-5" />
                    You have been removed from our mailing list. Honored within 10 business days as required by the CAN-SPAM Act.
                  </div>
                ) : (
                  <Button
                    onClick={submit}
                    disabled={status === 'loading'}
                    size="lg"
                    className="w-full"
                    data-testid="unsubscribe-confirm-btn"
                  >
                    {status === 'loading' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing</>
                    ) : 'Confirm unsubscribe'}
                  </Button>
                )}
                <p className="text-[11px] text-muted-foreground pt-4 border-t border-border">
                  Transactional messages (rate confirmations, invoices, packet uploads) will continue regardless.
                  Only commercial outreach is suppressed.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <PlatformDisclaimer />
    </div>
  );
};

export default UnsubscribePage;
